import { If } from '@finn/design-system/atoms/If';
import { Spinner } from '@finn/design-system/atoms/spinner';
import {
  Features,
  useDownPayment,
  useIsABVariant,
} from '@finn/ua-featureflags';
import { useCurrentLocale } from '@finn/ui-utils';
import { ReactNode, useMemo } from 'react';

import {
  FiltersResponse,
  FilterValuesObject,
  GenericVehicleDetails,
  QueryFilterMapper,
} from '../core';
import {
  CampaignCard,
  CampaignCardProps,
  CampaignCardWithType,
  CombinedItem,
  ItemType,
  ProductCard,
  useCampaignCards,
} from '../product-cards';
import { ModelCard } from '../product-cards/ModelCard';
import { RetentionCards } from '../retention';
import { Pagination } from './components/Pagination';
import { ProductListInfiniteScrollTrigger } from './components/ProductListInfiniteScrollTrigger';
import { ZeroProduct } from './components/ZeroProduct';
import { useCheapestPrices } from './hooks/useCheapestPrices';

type ProductListingProps = {
  status: 'loading' | 'success' | 'error' | 'empty' | 'validating';
  items: GenericVehicleDetails[];
  campaignData?: CampaignCardProps[];
  partnerDiscount?: number;
  paginationType: 'infinite' | 'pagination';
  page?: number;
  total?: number;
  limit?: number;
  fetchNextPage?: () => void;
  children?: ReactNode;
  filterValues?: FilterValuesObject;
  availableFilters?: FiltersResponse;
  isOutOfStock?: boolean;
  withRetentionCards?: boolean;
  onMessage?: (
    type: 'reset_filters',
    item?: GenericVehicleDetails,
    position?: number
  ) => void;
  CosmicModule?: any;
};

export const mergeVehiclesAndCampaignCards = (
  vehicles: GenericVehicleDetails[],
  campaigns?: CampaignCardProps[]
): CombinedItem[] => {
  const mergedList: CombinedItem[] = vehicles.map((vehicle, index) => ({
    ...vehicle,
    type: ItemType.VEHICLE,
    position: index,
  }));

  campaigns?.forEach((campaign) => {
    const position = Number(campaign.metadata.default_position) - 1;

    mergedList.splice(position, 0, {
      ...campaign,
      type: ItemType.CAMPAIGN_CARD,
    });
  });

  return mergedList;
};

// Type guards
export const isCampaignCard = (
  item: CombinedItem
): item is CampaignCardWithType => {
  return item.type === ItemType.CAMPAIGN_CARD;
};

export const ProductListing = ({
  status,
  items,
  campaignData,
  partnerDiscount,
  page,
  total,
  limit,
  paginationType,
  filterValues = {},
  // withRetentionCards -> requres nextjs session
  withRetentionCards = true,
  availableFilters,
  fetchNextPage,
  onMessage,
  isOutOfStock,
  CosmicModule,
}: ProductListingProps) => {
  const campaignCards = useCampaignCards(campaignData, isOutOfStock);
  const { isDE } = useCurrentLocale();
  const isPlpAdvancedGrouping = useIsABVariant(Features.ExpPlpAdvancedGrouping);
  const brandModelMap = useCheapestPrices(
    isPlpAdvancedGrouping,
    availableFilters
  );

  const urlParams = useMemo(() => {
    const queryMapper = new QueryFilterMapper('');

    return queryMapper.serialize(filterValues);
  }, [filterValues]);

  const { terms } = filterValues;
  const selectedTerm = terms?.[0];
  const combinedList = useMemo(
    () => mergeVehiclesAndCampaignCards(items, campaignCards),
    [items, campaignCards]
  );

  return (
    <div className="flex flex-wrap gap-6">
      <If condition={status === 'loading'}>
        <div className="w-full">
          <div className="text-center">
            <Spinner className="size-12 fill-trustedBlue" />
          </div>
        </div>
      </If>
      <div className="w-full">
        {items.length ? (
          <div
            className={
              'grid w-full grid-cols-[repeat(2,minmax(120px,1fr))] gap-x-2 gap-y-6 md:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] md:gap-x-4 lg:grid-cols-[repeat(3,minmax(280px,1fr))]'
            }
          >
            {isDE && withRetentionCards && <RetentionCards />}

            {combinedList.map((item, index) => {
              if (isCampaignCard(item)) {
                return (
                  <div key={`campaign-${item.slug}-${index}`}>
                    <CampaignCard
                      CosmicModule={CosmicModule}
                      campaignData={item}
                    />
                  </div>
                );
              }
              const { cheapestPrice, versionsCount } =
                brandModelMap[`${item.brand?.id}::${item.model}`] ?? {};

              return (
                <div
                  key={`${item.id}-${item.brand?.id}-${index}`}
                  data-testid="product-card"
                  data-productid={item.id}
                >
                  {isPlpAdvancedGrouping ? (
                    <ModelCard
                      vehicle={item}
                      cheapestPrice={cheapestPrice}
                      versionsCount={versionsCount}
                      urlParams={urlParams}
                      partnerDiscount={partnerDiscount}
                      selectedTerm={selectedTerm}
                      position={index + 1}
                    />
                  ) : (
                    <ProductCard
                      urlParams={urlParams}
                      shouldPreload
                      vehicle={item}
                      position={index + 1}
                      selectedTerm={selectedTerm}
                      partnerDiscount={partnerDiscount}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <If condition={status !== 'loading' && status !== 'validating'}>
            <ZeroProduct
              filterValues={filterValues}
              onResetFilters={() => onMessage?.('reset_filters')}
            />
          </If>
        )}
      </div>
      <div className="flex basis-full justify-center">
        <If condition={paginationType === 'infinite'}>
          <ProductListInfiniteScrollTrigger
            isLoading={status !== 'loading' && status === 'validating'}
            loadNextPage={fetchNextPage}
          />
        </If>
        <If condition={paginationType === 'pagination' && Number(total) > 0}>
          <Pagination
            shallow
            disabled={status === 'loading'}
            page={page ?? 1}
            totalPages={Math.ceil(Number(total) / Number(limit))}
          />
        </If>
      </div>
    </div>
  );
};
